import React, {
  createContext,
  useEffect,
  useLayoutEffect,
  useState,
  useContext,
  useRef,
  useCallback,
} from "react";
import { getDatabase, ref, onValue } from "firebase/database";
import firebaseApp from "../firebaseConfig";
import { AuthContext } from "./AuthContext";
import { ClientContext } from "./ClientContext";
import { apiRequest } from "../api/baseApi";
import { addBreadcrumb, captureError } from "../config/sentry";

export const CallV3Context = createContext();

// Max remembered ended-call uids (late-duplicate re-ring guard). Exported so
// tests can pin the FIFO eviction behavior.
export const ENDED_CALL_GUARD_MAX = 50;

export const CallV3Provider = ({ children }) => {
  const { currentUser } = useContext(AuthContext);
  const { selectedClient } = useContext(ClientContext);

  const [callerName, setCallerName] = useState(null);
  const [callId, setCallId] = useState(null);
  const [callEndState, setCallEndState] = useState(null);
  const [callType, setCallType] = useState(null);
  const [inCall, setInCall] = useState(false);
  // GMA-1331: surface popup-blocked / init-failed / network failures to the
  // caller so they aren't left staring at a black screen. `kind` drives the
  // copy + action shown by <CallErrorSnackbar />. `retryUrl` is set when the
  // popup was blocked so the user can re-fire window.open from their click.
  const [placeCallError, setPlaceCallError] = useState(null);
  const onCallEndCallbackRef = useRef(null);

  // #443: do not offer popup retry for a call that already reached a terminal
  // end. busy/offline count as terminal too — retrying the popup for a callee
  // who is busy or offline would open a dead call window under a stale banner.
  useEffect(() => {
    if (
      callEndState === "ended" ||
      callEndState === "rejected" ||
      callEndState === "missed" ||
      callEndState === "cancelled" ||
      callEndState === "busy" ||
      callEndState === "offline"
    ) {
      setPlaceCallError(null);
    }
  }, [callEndState]);

  //AUDIO
  useEffect(() => {
    if (callerName && !inCall && !callEndState) {
      const audio = new Audio(
        "https://cdn.pixabay.com/audio/2021/08/04/audio_f727f1d9fc.mp3"
      );
      audio.loop = true;
      (async () => {
        try {
          await audio.play();
        } catch (error) {
          console.error("Error playing ringtone:", error);
        }
      })();
      return () => {
        audio.pause();
        audio.src = "";
      };
    }
  }, [callerName, inCall, callEndState]);

  //INCOMING CALL LISTENER
  useEffect(() => {
    if (!currentUser) return;

    const db = getDatabase(firebaseApp);
    const path = `users/${currentUser.id}`;
    const userRef = ref(db, path);

    const unsubscribe = onValue(userRef, (snapshot) => {
      const data = snapshot.val();
      if (data && data.callUid && data.callStatus === "pending") {
        setCallId(data.callUid);
        setCallerName(data.callerName);
        setCallType(data.callType || "normal");
      }
    });

    return () => {
      unsubscribe();
    };
  }, [currentUser]);

  // #318: calls whose end this window already processed. Two signals can
  // announce the same teardown — the RTDB calls/<id> removal AND the
  // call_cancelled web push (GMA-1655 / cortex-be#429) — so the teardown
  // below must run at most once per call. Also guards a late/duplicate
  // incoming_call push from re-ringing a call already dealt with here.
  const endedCallUidsRef = useRef(new Set());
  // Cap the guard set so a long-lived SPA session can't grow it unboundedly:
  // FIFO-evict the oldest entries (Set preserves insertion order). Recent
  // calls stay guarded against late duplicate pushes; ancient ones can't leak.
  const markCallEnded = useCallback((callUid) => {
    const ended = endedCallUidsRef.current;
    ended.add(callUid);
    while (ended.size > ENDED_CALL_GUARD_MAX) {
      ended.delete(ended.values().next().value);
    }
  }, []);

  // Shared teardown for a call that ended remotely (caller cancelled,
  // answered elsewhere, timed out). Fetches the authoritative outcome from
  // call_history so MissedCallModal shows the right copy — the exact
  // behavior the RTDB call-end listener always had, now also reachable from
  // the call_cancelled web push.
  const handleRemoteCallEnd = useCallback(async (endedCallId) => {
    if (!endedCallId || endedCallUidsRef.current.has(endedCallId)) return;
    // Mark as in-progress so concurrent signals don't double-run teardown, but
    // release the guard on any failure/non-ok so a later signal can retry — a
    // network/auth error must not permanently wedge the UI in a ringing state.
    markCallEnded(endedCallId);
    try {
      const historyResponse = await apiRequest(`call_history/${endedCallId}`, {
        method: "GET",
      });
      if (!historyResponse.ok) {
        endedCallUidsRef.current.delete(endedCallId);
        return;
      }
      const history = await historyResponse.json();
      if (history.status !== "busy") {
        setCallEndState(history.status);
      }
      setInCall(false);
    } catch (error) {
      endedCallUidsRef.current.delete(endedCallId);
      addBreadcrumb("call.push.web_remote_end_failed", {
        category: "call.lifecycle",
        level: "warning",
        callId: endedCallId,
        error: error?.message || String(error),
      });
    }
  }, [markCallEnded]);

  //CALL END LISTENER
  useEffect(() => {
    if (!callId || callId === 0) return;
    const db = getDatabase(firebaseApp);
    const path = `calls/${callId}`;
    const callRef = ref(db, path);
    const unsubscribe = onValue(callRef, async (snapshot) => {
      if (!snapshot.exists()) {
        await handleRemoteCallEnd(callId);
      }
    });
    return () => {
      unsubscribe();
    };
  }, [callId, handleRemoteCallEnd]);

  // GMA-1331: clear any prior error so the snackbar disappears once the
  // caller successfully starts a new call attempt.
  const clearPlaceCallError = () => setPlaceCallError(null);

  const openCall = useCallback((callIdToOpen, preopened = null) => {
    const url = `${window.location.origin}/call?callId=${encodeURIComponent(callIdToOpen)}`;
    // Prefer a window opened synchronously in the click gesture (placeCall /
    // answerCall); after awaits, Safari/Firefox often return null from
    // window.open even when the user clicked — which used to raise a false
    // "popup blocked" banner. NB: never pass "noopener"/"noreferrer" — that
    // also returns null on success. Anti-tabnabbing: null opener manually.
    // Call window.open directly (not a helper closed over this callback) so
    // react-hooks/exhaustive-deps stays clean under lint:strict.
    let popup =
      preopened && !preopened.closed ? preopened : null;
    if (popup) {
      try {
        popup.location = url;
      } catch (_err) {
        // Cross-origin about:blank navigation can throw in some embeds;
        // fall through to a fresh open below.
        popup = null;
      }
    }
    if (!popup) {
      popup = window.open(url, "_blank");
    }
    const popupBlocked = !popup;
    addBreadcrumb("call.openCall", {
      category: "call.lifecycle",
      level: popupBlocked ? "warning" : "info",
      callId: callIdToOpen,
      popupBlocked,
      url,
      reusedPreopened: Boolean(preopened) && !preopened.closed,
    });
    if (popup) {
      popup.opener = null;
      popup.focus();
      setPlaceCallError(null);
      return true;
    }
    // GMA-1331: surface popup-blocked so the user has a one-click recovery
    // path. The retry must be invoked from a user gesture (button click)
    // so the browser allows the new window.
    setPlaceCallError({
      kind: "popup-blocked",
      message:
        "Your browser blocked the call window. Allow popups for this site or click below to open it.",
      retryUrl: url,
    });
    return false;
  }, []);

  const placeCall = useCallback(async () => {
    const receiverId = selectedClient?.id;
    setPlaceCallError(null);
    // FE-1E (#187): MissedCallModal "Call back" can invoke placeCall with no
    // selected client; guard the receiver deref so we never throw, and treat it
    // as a benign no-op (no error snackbar). GMA-1331 error-surfacing below.
    if (!receiverId) {
      addBreadcrumb("placeCall: no selected client to call", {
        category: "call.lifecycle.place_call.no_receiver",
        level: "warning",
        hasSelectedClient: Boolean(selectedClient),
      });
      return;
    }
    addBreadcrumb("call.placeCall", {
      category: "call.lifecycle",
      level: "info",
      stage: "start",
      receiverId,
      callType: "normal",
    });
    // Open inside the click's user-gesture window — after the awaits below
    // the gesture has lapsed and browsers return null even though the user
    // clicked. Navigate the placeholder once call/init resolves; close it on
    // 409/error so no orphan about:blank tab remains.
    const preopened = window.open("about:blank", "_blank");
    let response;
    try {
      response = await apiRequest("call/init", {
        method: "POST",
        body: { receiverId, callType: "normal" },
        version: "v3",
      });
    } catch (err) {
      if (preopened && !preopened.closed) preopened.close();
      const isLikelyNetworkFailure =
        err instanceof TypeError ||
        (err && typeof err.message === "string" &&
          /failed to fetch|network|load failed/i.test(err.message));
      addBreadcrumb("call.placeCall", {
        category: "call.lifecycle",
        level: "error",
        stage: isLikelyNetworkFailure ? "network-error" : "request-error",
        receiverId,
      });
      captureError(err, {
        tags: {
          feature: "call.placeCall",
          failure: isLikelyNetworkFailure ? "network" : "unexpected",
        },
        extra: { receiverId },
      });
      setPlaceCallError({
        kind: isLikelyNetworkFailure ? "network" : "init-failed",
        message: isLikelyNetworkFailure
          ? "Couldn't start call — network problem. Check your connection and try again."
          : "Couldn't start call — an unexpected error occurred. Please try again.",
      });
      return;
    }
    if (response.ok) {
      const data = await response.json();
      if (data.callId) {
        setCallId(data.callId);
        const opened = openCall(
          data.callId,
          preopened && !preopened.closed ? preopened : null,
        );
        if (opened) {
          addBreadcrumb("call.placeCall", {
            category: "call.lifecycle",
            level: "info",
            stage: "ok",
            receiverId,
            callId: data.callId,
          });
        } else if (preopened && !preopened.closed) {
          preopened.close();
        }
        return;
      }
      if (preopened && !preopened.closed) preopened.close();
      // GMA-1331 (Copilot #171): ok without callId is still an init failure —
      // otherwise placeCall silently returns and the caregiver sees no window.
      addBreadcrumb("call.placeCall", {
        category: "call.lifecycle",
        level: "error",
        stage: "init-missing-call-id",
        receiverId,
      });
      captureError(new Error("call/init returned ok but missing callId"), {
        tags: { feature: "call.placeCall", failure: "init-missing-call-id" },
        extra: { receiverId },
      });
      setPlaceCallError({
        kind: "init-failed",
        message:
          "Couldn't start call — the server didn't return a call ID. Please try again.",
      });
      return;
    }
    if (response.status === 409) {
      if (preopened && !preopened.closed) preopened.close();
      const errorData = await response.json();
      setCallId(0);
      setCallEndState(
        errorData.ended_status || errorData.userStatus || "busy"
      );
      return;
    }
    if (preopened && !preopened.closed) preopened.close();
    addBreadcrumb("call.placeCall", {
      category: "call.lifecycle",
      level: "error",
      stage: "init-failed",
      receiverId,
      status: response.status,
    });
    captureError(new Error(`call/init returned ${response.status}`), {
      tags: { feature: "call.placeCall", failure: "init-non2xx" },
      extra: { receiverId, status: response.status },
    });
    setPlaceCallError({
      kind: "init-failed",
      message: `Couldn't start call — server returned ${response.status}. Please try again.`,
    });
  }, [openCall, selectedClient]);

  const answerCall = async () => {
    // Same gesture-window rule as placeCall: open before the await.
    const preopened = window.open("about:blank", "_blank");
    try {
      const response = await apiRequest("call/answer", {
        method: "POST",
        body: {
          callId,
        },
        version: "v3",
      });
      if (response.ok) {
        // Ignore a placeholder the user closed while answer was in flight.
        const opened = openCall(
          callId,
          preopened && !preopened.closed ? preopened : null,
        );
        if (opened) {
          setInCall(true);
        } else if (preopened && !preopened.closed) {
          // openCall already surfaced popup-blocked; close the about:blank
          // placeholder so we don't leave an orphan tab or mark in-call.
          preopened.close();
        }
      } else if (preopened && !preopened.closed) {
        preopened.close();
      }
      return response;
    } catch (error) {
      if (preopened && !preopened.closed) preopened.close();
      throw error;
    }
  };

  const endCall = async () => {
    return apiRequest("call/end", {
      method: "POST",
      body: {
        callId,
      },
      version: "v3",
    });
  };

  // #318 (companion to cortex-be#429): the service worker forwards
  // incoming_call / call_cancelled web pushes and notification Answer/Decline
  // clicks to open windows via postMessage. Everything routes into the
  // existing CallV3 state and flows — the push is just a second signal source
  // next to the Firebase RTDB listeners, deduped by callUid.
  //
  // Latest-ref pattern: one listener registered for the provider's lifetime,
  // dispatching to a closure rebuilt each render so it always sees fresh
  // state (callId/inCall/answerCall) without re-subscribing on every change.
  const swMessageHandlerRef = useRef(null);
  // useLayoutEffect (not useEffect): the ref must point at the fresh closure
  // synchronously at commit, before the browser can dispatch queued SW
  // message events to a stale one (back-to-back incoming_call/call_cancelled).
  useLayoutEffect(() => {
    swMessageHandlerRef.current = (message) => {
      if (!message || typeof message !== "object" || !message.callUid) return;

      if (message.type === "incoming_call") {
        if (inCall) return; // never clobber an active call
        if (endedCallUidsRef.current.has(message.callUid)) return; // late duplicate of a handled call
        if (callId === message.callUid) {
          // RTDB pending signal already rang this call — push loses.
          addBreadcrumb("call.push.web_incoming_deduped", {
            category: "call.lifecycle",
            level: "info",
            callId: message.callUid,
          });
          return;
        }
        addBreadcrumb("call.push.web_incoming_received", {
          category: "call.lifecycle",
          level: "info",
          callId: message.callUid,
          callType: message.callType || "normal",
        });
        setCallId(message.callUid);
        setCallerName(message.callerName || "Someone");
        setCallType(message.callType || "normal");
        return;
      }

      if (message.type === "call_cancelled") {
        if (callId !== message.callUid || inCall) return;
        addBreadcrumb("call.push.web_cancel_received", {
          category: "call.lifecycle",
          level: "info",
          callId: message.callUid,
          reason: message.reason || "",
        });
        // handleRemoteCallEnd is failure-safe internally (releases its dedupe
        // guard and breadcrumbs on error), so it can never reject — void marks
        // this as intentional fire-and-forget from the SW-message handler.
        void handleRemoteCallEnd(message.callUid);
        return;
      }

      if (message.type === "incoming_call_answer") {
        if (callId !== message.callUid || inCall || callEndState) return;
        addBreadcrumb("call.push.web_notification_answer", {
          category: "call.lifecycle",
          level: "info",
          callId: message.callUid,
        });
        void answerCall().catch((error) => {
          addBreadcrumb("call.push.web_notification_answer_failed", {
            category: "call.lifecycle",
            level: "warning",
            callId: message.callUid,
            error: error?.message || String(error),
          });
        });
        return;
      }

      if (message.type === "incoming_call_decline") {
        if (callId !== message.callUid || inCall || callEndState) return;
        addBreadcrumb("call.push.web_notification_decline", {
          category: "call.lifecycle",
          level: "info",
          callId: message.callUid,
        });
        // Same flow as CallReceiver's Reject button; mark the call ended so
        // a late duplicate incoming_call push cannot re-ring it, but release
        // the guard if endCall fails so later teardown signals (RTDB removal
        // / call_cancelled push) can still retry — same pattern as
        // handleRemoteCallEnd above.
        markCallEnded(message.callUid);
        endCall()
          .then(() => clearEndCallState())
          .catch((error) => {
            endedCallUidsRef.current.delete(message.callUid);
            addBreadcrumb("call.push.web_notification_decline_failed", {
              category: "call.lifecycle",
              level: "warning",
              callId: message.callUid,
              error: error?.message || String(error),
            });
          });
      }
    };
  });

  useEffect(() => {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
      return undefined;
    }
    const serviceWorkerContainer = navigator.serviceWorker;
    const onSwMessage = (event) => {
      if (swMessageHandlerRef.current) {
        swMessageHandlerRef.current(event.data);
      }
    };
    serviceWorkerContainer.addEventListener("message", onSwMessage);
    return () => {
      serviceWorkerContainer.removeEventListener("message", onSwMessage);
    };
  }, []);

  const submitCallDescription = async (description) => {
    const response = await apiRequest(`call_histories/${callId}`, {
      method: "PUT",
      body: { description },
    });
    return response;
  };

  const registerOnCallEnd = useCallback((callback) => {
    onCallEndCallbackRef.current = callback;
  }, []);

  const clearEndCallState = () => {
    if (onCallEndCallbackRef.current) {
      onCallEndCallbackRef.current();
    }
    setCallEndState(null);
    setCallId(null);
    setCallerName(null);
    setCallType(null);
  };

  return (
    <CallV3Context.Provider
      value={{
        callId,
        callEndState,
        callerName,
        callType,
        inCall,
        placeCall,
        answerCall,
        endCall,
        openCall,
        submitCallDescription,
        clearEndCallState,
        registerOnCallEnd,
        placeCallError,
        clearPlaceCallError,
      }}
    >
      {children}
    </CallV3Context.Provider>
  );
};
